import React from 'react';
import { motion } from 'framer-motion';

const InvSlide06Gamification: React.FC = () => {
  const features = [
    { icon: '⭐', title: 'XP & Levels', desc: 'Earn points for every correct answer, level up over time' },
    { icon: '🔥', title: 'Streaks', desc: 'Build daily learning habits with streak tracking' },
    { icon: '🏆', title: 'Achievements', desc: 'Unlock badges for milestones and perfect scores' },
    { icon: '📊', title: 'Leaderboards', desc: 'Optional class competition (teacher-controlled)' },
    { icon: '🪙', title: 'Dual Currency', desc: 'Gold coins (school) + Silver coins (home)' },
    { icon: '🎁', title: 'Reward Shops', desc: 'Redeem coins for rewards defined by teacher/parent' },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        Gamification That Works
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center"
      >
        Duolingo + ClassDojo combined — kids want to come back
      </motion.p>

      <div className="flex gap-10 w-full max-w-6xl">
        {/* Left: Features Grid */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <div className="grid grid-cols-2 gap-4">
            {features.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.08 }}
                className="p-5 rounded-xl"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-[#94a3b8]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Right: Dual Currency Explanation */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="w-80"
        >
          <h3 className="text-xl font-semibold text-white mb-4">Dual Reward System</h3>

          {/* Gold Coins */}
          <div
            className="p-5 rounded-xl mb-4"
            style={{
              background: 'rgba(255, 215, 0, 0.1)',
              border: '2px solid rgba(255, 215, 0, 0.4)',
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">🪙</span>
              <span className="text-xl font-bold text-[#ffd700]">Gold Coins</span>
            </div>
            <div className="text-sm text-[#94a3b8] space-y-1">
              <p><span className="text-white">Earned:</span> Lessons, quizzes, achievements</p>
              <p><span className="text-white">Managed by:</span> Teacher</p>
              <p><span className="text-white">Redeemed for:</span> Extra recess, homework pass, prizes</p>
            </div>
          </div>

          {/* Silver Coins */}
          <div
            className="p-5 rounded-xl mb-4"
            style={{
              background: 'rgba(192, 192, 192, 0.1)',
              border: '2px solid rgba(192, 192, 192, 0.4)',
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">🥈</span>
              <span className="text-xl font-bold text-[#c0c0c0]">Silver Coins</span>
            </div>
            <div className="text-sm text-[#94a3b8] space-y-1">
              <p><span className="text-white">Earned:</span> Chores, good behavior at home</p>
              <p><span className="text-white">Managed by:</span> Parent</p>
              <p><span className="text-white">Redeemed for:</span> Screen time, choose dinner, stay up late</p>
            </div>
          </div>

          <div
            className="p-3 rounded-lg text-sm text-[#94a3b8]"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <span className="text-white font-medium">Why separate?</span> Teachers control academic motivation. Parents extend engagement at home. No mixing.
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvSlide06Gamification;
